import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiCall } from "../Apis/apiCall";
import LoadingComponent from "../components/ui/LoadingComponent";
import TableDataNoAvailable from "./ui/TableDataNoAvailable";
import PaginationControls from "./ui/PaginationControls";
import PerPageDropdown from "./ui/PerPageDropdown";
import { Modal, Button } from "react-bootstrap";
import html2pdf from "html2pdf.js";
import SortIcons from "./ui/SortIcons";
import ErrorAlert from "./ui/ErrorAlert";
import { handleApiResponse, handleCustomResponse } from "../utils/response";

const BillingSummaryLayer = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [invoiceHTML, setInvoiceHTML] = useState("");
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState("");
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });
  const navigate = useNavigate();

  const fetchClients = async (
    page = 1,
    perPageValue = 10,
    search = "",
    sortBy = null,
    sortOrder = "asc"
  ) => {
    setLoading(true);
    setErrorMessage("");
    
    try {
      const queryParams = new URLSearchParams({
        page: page,
        per_page: perPageValue,
      });
      
      if (search) queryParams.append("search", encodeURIComponent(search));
      if (sortBy) queryParams.append("sort_by", sortBy);
      if (sortOrder) queryParams.append("sort_order", sortOrder);

      const response = await apiCall(
        "get",
        `/get-all-client-usage?${queryParams.toString()}`,
        null,
        navigate
      );

      if (response?.data?.success) {
        let clientList = [];
        let total = 0;
        let currentPageFromResponse = page;
        let lastPage = 1;

        const data = response.data;

        if (data.pagination) {
          clientList = data.data || [];
          total = data.pagination.total || 0;
          currentPageFromResponse = data.pagination.current_page || page;
          lastPage = data.pagination.last_page || 1;
        } else if (Array.isArray(data.data)) {
          clientList = data.data;
          total = data.total || clientList.length;
          currentPageFromResponse = data.current_page || page;
          lastPage = data.last_page || Math.ceil(total / perPageValue);
        } else if (data.data?.data) {
          clientList = data.data.data;
          total = data.data.total || 0;
          currentPageFromResponse = data.data.current_page || page;
          lastPage = data.data.last_page || 1;
        }

        setClients(clientList);
        setTotalEntries(total);
        setTotalPages(parseInt(lastPage, 10));
        setCurrentPage(parseInt(currentPageFromResponse, 10));
        
        handleApiResponse(response.data, "success", "GET");
      } else {
        handleCustomResponse("error", "Failed to load clients.");
        setErrorMessage(response?.data?.message || "Failed to load clients.");
      }
    } catch (error) {
      handleApiResponse(error.response?.data, "Err", "GET");
      setErrorMessage(error.response?.data?.message || "Error fetching clients.");
    } finally {
      setLoading(false);
    }
  };

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  const debouncedFetchClients = useCallback(
    debounce((page, perPageValue, search, sortField, sortDirection) => {
      fetchClients(page, perPageValue, search, sortField, sortDirection);
    }, 300),
    []
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setCurrentPage(1);
    debouncedFetchClients(1, perPage, value, sortConfig.field, sortConfig.direction);
  };

  const handleSort = (field) => {
    const newDirection =
      sortConfig.field === field && sortConfig.direction === "asc"
        ? "desc"
        : "asc";
    setSortConfig({ field, direction: newDirection });
    setCurrentPage(1);
    fetchClients(1, perPage, searchQuery, field, newDirection);
  };

  useEffect(() => {
    fetchClients(currentPage, perPage, searchQuery, sortConfig.field, sortConfig.direction);
  }, [currentPage, perPage]);

  const handleViewInvoice = (clientData) => {
    try {
      setModalLoading(true);
      setModalError("");
      const invoiceContent = generateInvoiceHTML(clientData);
      setInvoiceHTML(invoiceContent);
      setShowModal(true);
    } catch (error) {
      setModalError("Error generating invoice.");
    } finally {
      setModalLoading(false);
    }
  };

  const handleDownloadInvoice = () => {
    const element = document.getElementById("invoice-content");
    const opt = {
      margin: [0.5, 0.5, 0.5, 0.5],
      filename: `Invoice-${new Date().toISOString().split("T")[0]}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: {
        unit: "in",
        format: "letter",
        orientation: "portrait",
        compress: true,
      },
      pagebreak: { mode: ["avoid-all", "css", "legacy"] },
    };
    html2pdf().from(element).set(opt).save();
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setInvoiceHTML("");
    setModalError("");
  };

  const generateInvoiceHTML = (client) => {
    const today = new Date().toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    const monthYear = client.month
      ? new Date(client.month).toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        })
      : "-";

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Invoice - ${client.client_name}</title>
        <style>
          :root {
            --primary-600: #E63C23;
            --neutral-50: #fafafa;
            --neutral-100: #f5f5f5;
            --neutral-200: #e0e0e0;
            --neutral-500: #757575;
            --neutral-900: #212121;
            --success-100: #e8f5e9;
            --success-600: #4caf50;
            --success-700: #388e3c;
            --warning-100: #fff3e0;
            --warning-600: #f57c00;
            --warning-700: #ef6c00;
            --white: #ffffff;
            --shadow-1: 0 2px 8px rgba(0, 0, 0, 0.1);
            --rounded-6: 6px;
            --font-xs: 11px;
            --font-sm: 13px;
            --font-md: 15px;
            --font-lg: 17px;
            --font-xl: 22px;
            --font-xxl: 28px;
          }

          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }

          body {
            font-family: 'Arial', sans-serif;
            background: #f4f6f9;
            padding: 20px;
            line-height: 1.5;
            font-size: var(--font-sm);
          }

          .invoice-container {
            max-width: 800px;
            margin: 0 auto;
            background: var(--white);
            padding: 30px;
            border-radius: 10px;
            box-shadow: var(--shadow-1);
            min-height: 9in; /* Ensure content fits within letter size */
            page-break-inside: avoid;
          }

          .invoice-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
            padding-bottom: 15px;
            border-bottom: 2px solid var(--primary-600);
          }

          .company-info h1 {
            color: var(--primary-600);
            font-size: var(--font-xl);
            font-weight: 700;
            margin-bottom: 8px;
          }

          .company-info p {
            color: var(--neutral-500);
            font-size: var(--font-xs);
            line-height: 1.5;
          }

          .invoice-details {
            text-align: right;
          }

          .invoice-details h4 {
            color: var(--neutral-900);
            font-size: var(--font-md);
            font-weight: 600;
            margin-bottom: 10px;
            text-transform: uppercase;
            letter-spacing: 0.8px;
          }

          .invoice-details p {
            color: var(--neutral-500);
            font-size: var(--font-xs);
            margin: 5px 0;
          }

          .client-info {
            margin-bottom: 20px;
            padding: 15px;
            background: var(--neutral-50);
            border-radius: var(--rounded-6);
            border-left: 4px solid var(--primary-600);
          }

          .client-info h4 {
            color: var(--primary-600);
            font-size: var(--font-md);
            font-weight: 600;
            margin-bottom: 10px;
          }

          .client-info p {
            color: var(--neutral-900);
            font-size: var(--font-xs);
            margin: 5px 0;
          }

          .invoice-table {
            width: 100%;
            margin: 20px 0;
            border-collapse: collapse;
            background: var(--white);
          }

          .invoice-table thead {
            background: var(--primary-600);
            color: var(--white);
          }

          .invoice-table th {
            padding: 10px 14px;
            text-align: left;
            font-size: var(--font-xs);
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .invoice-table td {
            padding: 10px 14px;
            border-bottom: 1px solid var(--neutral-200);
            font-size: var(--font-xs);
            color: var(--neutral-900);
            vertical-align: middle;
          }

          .invoice-table tbody tr:hover {
            background: var(--neutral-100);
            transition: background 0.2s ease;
          }

          .totals {
            margin-top: 20px;
            text-align: right;
          }

          .totals-table {
            margin-left: auto;
            width: 280px;
            border-collapse: collapse;
          }

          .totals-table tr {
            border-bottom: 1px solid var(--neutral-200);
          }

          .totals-table td {
            padding: 10px 14px;
            font-size: var(--font-xs);
          }

          .totals-table td:first-child {
            color: var(--neutral-500);
            text-align: left;
            font-weight: 500;
          }

          .totals-table td:last-child {
            text-align: right;
            font-weight: 600;
            color: var(--neutral-900);
          }

          .total-row {
            background: var(--primary-600);
            color: var(--white);
          }

          .total-row td {
            padding: 12px 14px;
            font-size: var(--font-sm);
            font-weight: 700;
            color: var(--white);
          }

          .status-badge {
            display: inline-block;
            padding: 5px 14px;
            border-radius: 20px;
            font-size: var(--font-xs);
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .status-paid {
            background: var(--success-100);
            color: var(--success-700);
          }

          .status-pending {
            background: var(--warning-100);
            color: var(--warning-700);
          }

          .payment-status {
            margin-top: 20px;
            padding: 15px;
            border-radius: var(--rounded-6);
            font-size: var(--font-xs);
          }

          .payment-status.paid {
            background: var(--success-100);
            border-left: 4px solid var(--success-600);
            color: var(--success-700);
          }

          .payment-status.pending {
            background: var(--warning-100);
            border-left: 4px solid var(--warning-600);
            color: var(--warning-700);
          }

          .footer {
            margin-top: 30px;
            padding-top: 15px;
            border-top: 1px solid var(--neutral-200);
            text-align: center;
            color: var(--neutral-500);
            font-size: var(--font-xs);
            line-height: 1.5;
          }

          @media print {
            body {
              padding: 0;
              background: var(--white);
            }
            .invoice-container {
              box-shadow: none;
              padding: 15px;
              border-radius: 0;
              max-width: 100%;
              min-height: auto;
            }
            .invoice-table th,
            .invoice-table td {
              padding: 8px 12px;
            }
            .invoice-header,
            .client-info,
            .totals,
            .payment-status,
            .footer {
              page-break-inside: avoid;
            }
          }
        </style>
      </head>
      <body>
        <div id="invoice-content" class="invoice-container">
          <div class="invoice-header">
            <div class="company-info">
              <h1>Park Vision</h1>
              <p>7th Floor Royal Bank Building</p>
              <p>2010 11th Ave, Regina, Saskatchewan, Canada S4P 0J3</p>
              <p>Phone: (123) 456-7890</p>
              <p>Email: info@parkeasesolutions.com</p>
            </div>
            <div class="invoice-details">
              <h4>INVOICE</h4>
              <p><strong>Invoice #:</strong> INV-${client.client_id}-${new Date().getFullYear()}</p>
              <p><strong>Date:</strong> ${today}</p>
              <p><strong>Status:</strong> 
                <span class="status-badge status-${client.invoice_status}">
                  ${client.invoice_status}
                </span>
              </p>
            </div>
          </div>

          <div class="client-info">
            <h4>Bill To:</h4>
            <p><strong>${client.client_name}</strong></p>
            <p>Client ID: ${client.client_id}</p>
            <p>Plan: ${client.plan_name}</p>
            <p>Billing Period: ${monthYear}</p>
          </div>

          <table class="invoice-table">
            <thead>
              <tr>
                <th>Description</th>
                <th style="text-align: center;">Quantity</th>
                <th style="text-align: right;">Rate</th>
                <th style="text-align: right;">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <strong>${client.plan_name} - ${monthYear}</strong><br>
                  <small style="color: var(--neutral-500);">Vehicle Count Allowed: ${client.vehicle_count_allowed}</small>
                </td>
                <td style="text-align: center;">1</td>
                <td style="text-align: right;">Base Plan</td>
                <td style="text-align: right;">Included</td>
              </tr>
              ${
                client.billable_vehicles > 0
                  ? `
              <tr>
                <td>
                  <strong>Additional Billable Vehicles</strong><br>
                  <small style="color: var(--neutral-500);">Threshold Crossed: ${client.threshold_crossed_count} times</small>
                </td>
                <td style="text-align: center;">${client.billable_vehicles}</td>
                <td style="text-align: right;">${(
                  parseFloat(client.amount_due.replace(/[$,]/g, "")) /
                  client.billable_vehicles
                ).toFixed(2)}</td>
                <td style="text-align: right;">${client.amount_due}</td>
              </tr>
              `
                  : ""
              }
            </tbody>
          </table>

          <div class="totals">
            <table class="totals-table">
              <tr>
                <td>Subtotal:</td>
                <td>${client.amount_due}</td>
              </tr>
              <tr>
                <td>Tax (0%):</td>
                <td>$0.00</td>
              </tr>
              <tr class="total-row">
                <td>Total Due:</td>
                <td>${client.amount_due}</td>
              </tr>
            </table>
          </div>

          <div class="payment-status ${client.paid_at ? "paid" : "pending"}">
            <p style="margin: 0; font-weight: 600;">
              ${client.paid_at ? "✓ Payment Received" : "⚠ Payment Pending"}
            </p>
            <p style="margin: 5px 0 0 0;">
              ${
                client.paid_at
                  ? `Paid on: ${new Date(client.paid_at).toLocaleDateString(
                      "en-US",
                      { year: "numeric", month: "long", day: "numeric" }
                    )}`
                  : "Please process payment at your earliest convenience."
              }
            </p>
          </div>

          <div class="footer">
            <p>Thank you for your business!</p>
            <p>For queries, contact us at info@parkeasesolutions.com</p>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const getClientLogo = (url) =>
    url
      ? `https://pevbackend.codovio.com/public/${url}`
      : "/Uploads/client_logos/";

  const handlePageChange = (page) => {
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handlePerPageChange = (value) => {
    setPerPage(value);
    setCurrentPage(1);
  };

  if (errorMessage && clients.length === 0) {
    return <ErrorAlert />;
  }

  return (
    <div className="p-4 p-md-3 p-sm-2">
      <style>{`
        .table-header {
          background-color: var(--primary-600);
          color: white;
        }
        .invoice-button {
          width: 32px;
          height: 32px;
          margin-right: 8px;
          color: var(--primary-600);
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: none;
          cursor: pointer;
        }
      `}</style>
      <div className="card shadow-sm">
        <div className="card p-3 p-sm-2 p-md-3">
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
            <div className="icon-field" style={{ maxWidth: "400px" }}>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search By Client Name"
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <span className="icon">
                <Icon icon="ion:search-outline" />
              </span>
            </div>
            <PerPageDropdown
              perPage={perPage}
              handlePerPageChange={handlePerPageChange}
            />
          </div>

          {errorMessage && (
            <div
              className="alert alert-danger alert-dismissible fade show mb-3"
              role="alert"
            >
              {errorMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setErrorMessage("")}
              ></button>
            </div>
          )}

          {clients.length === 0 && !loading ? (
            <TableDataNoAvailable message="No Summary found." />
          ) : (
            <div className="position-relative">
              <div
                className="table-responsive scroll-sm"
                style={{
                  opacity: loading ? 0.4 : 1,
                  pointerEvents: loading ? "none" : "auto",
                }}
              >
                <table className="table bordered-table mb-0 table-hover">
                  
                  <thead>
                    <tr className="table-header">
                      <th scope="col" className="p-2">
                        <span>S.No</span>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("client_name")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Client Name</span>
                          <SortIcons
                            field="client_name"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("plan_name")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Plan</span>
                          <SortIcons
                            field="plan_name"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("month")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Month</span>
                          <SortIcons field="month" sortConfig={sortConfig} />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("vehicle_count_allowed")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Vehicle Allowed</span>
                          <SortIcons
                            field="vehicle_count_allowed"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("threshold_crossed_count")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Threshold Crossed</span>
                          <SortIcons
                            field="threshold_crossed_count"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("billable_vehicles")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Billable Vehicles</span>
                          <SortIcons
                            field="billable_vehicles"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("amount_due")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Amount Due</span>
                          <SortIcons
                            field="amount_due"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("invoice_status")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Status</span>
                          <SortIcons
                            field="invoice_status"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("created_at")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Created Date</span>
                          <SortIcons
                            field="created_at"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th scope="col" className="p-2">
                        <span>Invoice</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map((client, idx) => (
                     <tr key={client.id || `${client.client_id}-${idx}`}>
                        <td className="p-2">
                          {(currentPage - 1) * perPage + idx + 1}
                        </td>
                        <td className="p-2 text-primary-600">
                          <Link to={"/all-clients"}>
                            {client?.client_name || "-"}
                          </Link>
                        </td>
                        <td className="p-2 text-primary-600">
                          <Link to={"/all-plans"}>
                            {client?.plan_name || "-"}
                          </Link>
                        </td>
                         <td className="p-2">
                          {client?.month
                            ? new Date(client.month).toLocaleDateString(
                                "en-US",
                                {
                                  month: "long",
                                  year: "numeric",
                                }
                              )
                            : "-"}
                        </td>
                        <td className="p-2">
                          {client?.vehicle_count_allowed !== undefined &&
                          client?.vehicle_count_allowed !== null
                            ? client.vehicle_count_allowed
                            : "-"}
                        </td>
                        <td className="p-2">
                          {client?.threshold_crossed_count !== undefined &&
                          client?.threshold_crossed_count !== null
                            ? client.threshold_crossed_count
                            : "-"}
                        </td>
                        <td className="p-2">
                          {client?.billable_vehicles !== undefined &&
                          client?.billable_vehicles !== null
                            ? client.billable_vehicles
                            : "-"}
                        </td>
                        <td className="p-2">
                          <span className="fw-semibold">
                            {client?.amount_due || "-"}
                          </span>
                        </td>
                        <td className="p-2">
                          <span
                            className={`badge ${
                              client?.invoice_status === "paid"
                                ? " text-success-700"
                                : client?.invoice_status === "pending"
                                ? "text-warning-700"
                                : " text-danger-700"
                            } px-2 py-1 rounded-pill`}
                            style={{
                              fontSize: "12px",
                              textTransform: "capitalize",
                            }}
                          >
                            {client?.invoice_status || "-"}
                          </span>
                        </td>
                       
                        <td className="p-2">
                          {client.created_at
                            ? new Date(client.created_at).toLocaleDateString(
                                "en-GB",
                                {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                }
                              )
                            : "-"}
                        </td>
                        <td className="p-2">
                          <button
                            className="invoice-button"
                            onClick={() => handleViewInvoice(client)}
                            title="View Invoice"
                          >
                            <Icon icon="iconamoon:eye-light" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {loading && (
                <div
                  className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
                  style={{
                    zIndex: 10,
                    borderRadius: "10px",
                    padding: "20px",
                  }}
                >
                  <LoadingComponent />
                </div>
              )}
              <PaginationControls
                pagination={{
                  currentPage,
                  perPage,
                  total: totalEntries,
                }}
                handlePageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>

      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Invoice</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {modalLoading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: "50vh" }}
            >
              <LoadingComponent />
            </div>
          ) : modalError ? (
            <div className="alert alert-danger" role="alert">
              {modalError}
            </div>
          ) : (
            <div dangerouslySetInnerHTML={{ __html: invoiceHTML }} />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={handleDownloadInvoice}
            disabled={modalLoading || modalError}
          >
            Download PDF
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BillingSummaryLayer;